import { obtainableFlowers } from './availability';
import { FLOWERS, type FlowerId } from './flowers';
import type { Rng } from './rng';
import type { GameState, Order, OrderItem } from './state';

export interface Customer {
  name: string;
  emoji: string;
}

/** The friendly critters of the plains who send in flower orders. */
export const CUSTOMERS: readonly Customer[] = [
  { name: 'Clover Bunny', emoji: '🐰' },
  { name: 'Fennel Fox', emoji: '🦊' },
  { name: 'Barley Bear', emoji: '🐻' },
  { name: 'Hazel Hedgehog', emoji: '🦔' },
  { name: 'Professor Hoot', emoji: '🦉' },
  { name: 'Acorn Squirrel', emoji: '🐿️' },
  { name: 'Lily Frog', emoji: '🐸' },
  { name: 'Wooly Sheep', emoji: '🐑' },
  { name: 'Duchess Duck', emoji: '🦆' },
  { name: 'Mochi Cat', emoji: '🐱' },
];

export function customerFor(order: Order): Customer {
  return CUSTOMERS[order.customer] ?? CUSTOMERS[0]!;
}

function quantityFor(species: FlowerId, rng: Rng): number {
  const price = FLOWERS[species].sellPrice;
  if (price <= 25) return rng.int(2, 5);
  if (price <= 150) return rng.int(1, 3);
  return rng.int(1, 2);
}

export function generateOrder(state: GameState, rng: Rng): Order {
  const pool = obtainableFlowers(state);
  const roll = rng.next();
  const itemCount = Math.min(pool.length, roll < 0.6 ? 1 : roll < 0.9 ? 2 : 3);
  const items: OrderItem[] = rng
    .sample(pool, itemCount)
    .map((species) => ({ species, qty: quantityFor(species, rng) }));

  const value = items.reduce((sum, item) => sum + FLOWERS[item.species].sellPrice * item.qty, 0);
  const xp = items.reduce((sum, item) => sum + FLOWERS[item.species].xp * item.qty, 0);

  return {
    id: state.nextOrderId++,
    customer: rng.int(0, CUSTOMERS.length - 1),
    items,
    reward: Math.round(value * (1.5 + 0.15 * (items.length - 1))),
    xp: Math.round(xp * 1.2) + 3,
  };
}

/** Whether the farm currently holds enough flowers (normal or golden) for an order. */
export function canFulfill(state: GameState, order: Order): boolean {
  return order.items.every((item) => {
    const stock = state.flowers[item.species];
    return stock !== undefined && stock.n + stock.g >= item.qty;
  });
}
